import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
interface WorkshopShotProps {
  src: string;
  alt: string;
  tag: string;
  title: string;
  body: string;
  delay?: number;
  offsetTop?: boolean;
}
function WorkshopShot({
  src,
  alt,
  tag,
  title,
  body,
  delay = 0,
  offsetTop
}: WorkshopShotProps) {
  return <motion.figure initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.8,
    delay
  }} className={`group flex flex-col bg-surface border border-gold/15 ${offsetTop ? 'md:mt-16' : ''}`} data-id="element-483">
      <div className="relative aspect-[4/5] overflow-hidden bg-paper" data-id="element-484">
        <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" data-id="element-485" />
        <div className="absolute top-5 left-5" data-id="element-486">
          <span className="px-3 py-1.5 bg-background/95 backdrop-blur brand-mark text-[0.6rem] uppercase text-gold-dark font-semibold" data-id="element-487">
            {tag}
          </span>
        </div>
      </div>
      <figcaption className="p-6 md:p-7 border-t border-gold/15" data-id="element-488">
        <p className="font-serif text-xl md:text-2xl text-primary leading-snug mb-2" data-id="element-489">
          {title}
        </p>
        <p className="text-sm text-secondary leading-relaxed text-pretty" data-id="element-490">
          {body}
        </p>
      </figcaption>
    </motion.figure>;
}
interface CommissionProps {
  src: string;
  alt: string;
  title: string;
  meta: string;
  year: string;
  delay?: number;
}
function CommissionCard({
  src,
  alt,
  title,
  meta,
  year,
  delay = 0
}: CommissionProps) {
  return <motion.figure initial={{
    opacity: 0,
    scale: 0.97
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.7,
    delay
  }} className="bg-surface border border-gold/15 flex flex-col" data-id="element-491">
      <div className="relative aspect-square overflow-hidden bg-paper" data-id="element-492">
        <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-contain p-8" data-id="element-493" />
      </div>
      <figcaption className="p-6 md:p-7 border-t border-gold/15 flex items-start justify-between gap-4" data-id="element-494">
        <div data-id="element-495">
          <p className="font-serif text-lg text-primary leading-snug mb-1" data-id="element-496">
            {title}
          </p>
          <p className="text-xs text-muted" data-id="element-497">{meta}</p>
        </div>
        <span className="brand-mark text-[0.6rem] text-gold-dark uppercase shrink-0" data-id="element-498">
          {year}
        </span>
      </figcaption>
    </motion.figure>;
}
export function OurWork() {
  return <section className="section-padding bg-background" data-id="element-499">
      <div className="container-custom" data-id="element-500">
        <div className="max-w-3xl mb-14" data-id="element-501">
          <span className="eyebrow" data-id="element-502">Our work · In our hands</span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.1] mb-5 text-balance" data-id="element-503">
            Every Sinclairs piece is made{' '}
            <span className="italic text-gold-dark" data-id="element-504">here</span> — by hand, on
            this bench.
          </h2>
          <p className="text-secondary text-lg max-w-2xl text-pretty" data-id="element-505">
            Not flown in from overseas. Not finished by an outsourced workshop.
            Designed in our shop, made on our bench, signed off by James before
            it ever leaves the building.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-10 mb-16 md:mb-20" data-id="element-506">
          <WorkshopShot src="https://cdn.magicpatterns.com/uploads/iPJgC3uU24qy6g4DSNZ3QQ/sn2.webp" alt="Stone-setting a ring by hand at the Sinclairs bench in Ardross" tag="At the bench" title="Hand-setting an heirloom stone." body="Most of our work is one pair of hands, one stone, one moment. The kind of jewellery-making that quietly outlasts trends." data-id="element-507" />
          <WorkshopShot src="https://cdn.magicpatterns.com/uploads/5vgMgR3JMFMrdtmBeJtrqm/sn4.webp" alt="Designing a custom engagement ring in CAD at Sinclairs Jewellers, Perth" tag="CAD · 3D proofing" title="You see it before we make it." body="Custom rings move from sketch to CAD to wearable 3D resin proof — so you hold and try the design before a single gram of gold is cast." delay={0.15} offsetTop data-id="element-508" />
        </div>

        {/* Recent commissions */}
        <div className="border-t border-gold/15 pt-14" data-id="element-509">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4" data-id="element-510">
            <div data-id="element-511">
              <span className="eyebrow" data-id="element-512">Recent Sinclairs commissions</span>
              <h3 className="font-serif text-2xl md:text-3xl font-medium leading-snug text-balance" data-id="element-513">
                Pieces we've handcrafted for Perth families this year.
              </h3>
            </div>
            <a href="#consultation" className="text-sm text-gold-dark font-medium hover:text-gold inline-flex items-center gap-2 group" data-id="element-514">
              Design something for you
              <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-515" />
            </a>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-10" data-id="element-516">
            <CommissionCard src="https://cdn.magicpatterns.com/uploads/w2f7n1Ys2Wayy5BDiaHQfc/d1-2.webp" alt="A custom marquise diamond engagement ring in 18ct yellow gold with hand-set pavé, made by Sinclairs Jewellers Perth" title='The "Marquise Twist"' meta="18ct yellow gold · marquise diamond · hand-set pavé" year="Custom · 2024" data-id="element-517" />
            <CommissionCard src="https://cdn.magicpatterns.com/uploads/i97S84Xv1qcHKn7jhaikdm/d2.webp" alt="A bespoke marquise diamond ring with bypass-style pavé shoulders in yellow gold, designed and made at Sinclairs in Ardross" title='The "Bypass Marquise"' meta="18ct yellow gold · marquise diamond · pavé shoulders" year="Custom · 2024" delay={0.1} data-id="element-518" />
          </div>

          <p className="text-xs text-muted italic text-center mt-10" data-id="element-519">
            [VERIFY] · Replace ring titles, years and descriptions with the
            client's preferred captions.
          </p>
        </div>
      </div>
    </section>;
}